import React, { useEffect, useState, useRef } from 'react';
import { Container, Row, Col, Card, Form, Button, Spinner } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import SignatureCanvas from 'react-signature-canvas';
import Swal from 'sweetalert2';
import {
    submitLunchPlanWaiver,
    selectWaiverById,
    selectSubmitLoading
} from '../../store/lunchPlanWaiver/lunchPlanWaiverSlice';
import { handleImageUpload } from '../../api/uploads';
import { getEffectiveStudentId } from '../../utilities/teacherModulePermissions';

const LunchPlanWaiverFormComp = ({ onSubmit, onCancel, onBack, remarks, defaultData }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { id } = useParams();
    const isEditMode = Boolean(id);

    const existingWaiver = useSelector((state) => selectWaiverById(state, id));
    const submitLoading = useSelector(selectSubmitLoading);
    let profileData = useSelector((state) => state.setting.setting.user_data);
    profileData = profileData ? JSON.parse(profileData) : null;

    const signaturePadRef = useRef(null);

    const [formData, setFormData] = useState({
        reason: '',
        parents_signature: '',
        parent_signature_date: ''
    });

    useEffect(() => {
        if (isEditMode && existingWaiver) {
            setFormData({
                reason: existingWaiver.reason || '',
                parents_signature: existingWaiver.parents_signature || '',
                parent_signature_date: existingWaiver.parent_signature_date || ''
            });
        }
    }, [isEditMode, existingWaiver]);

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleClearSignature = () => {
        if (signaturePadRef.current) {
            signaturePadRef.current.clear();
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validation
        if (!formData.reason) {
            Swal.fire({
                icon: 'warning',
                title: 'Required Field',
                text: 'Please provide a reason for the waiver.'
            });
            return;
        }

        const hasNewSignature = signaturePadRef.current && !signaturePadRef.current.isEmpty();
        const hasExistingSignature = isEditMode && existingWaiver?.parents_signature;

        if (!hasNewSignature && !hasExistingSignature) {
            Swal.fire({
                icon: 'warning',
                title: 'Signature Required',
                text: 'Please provide parent signature.'
            });
            return;
        }

        if (!formData.parent_signature_date) {
            Swal.fire({
                icon: 'warning',
                title: 'Date Required',
                text: 'Please provide signature date.'
            });
            return;
        }

        try {
            const submitData = { ...formData };

            // Upload new parent signature to AWS if provided
            if (hasNewSignature) {
                const signatureDataURL = signaturePadRef.current.toDataURL('image/png');
                const signatureBlob = await fetch(signatureDataURL).then(res => res.blob());
                const signatureFile = new File([signatureBlob], `parent_signature_${Date.now()}.png`, { type: 'image/png' });
                const signatureUrl = await handleImageUpload(signatureFile, 'signatures/', 'image/png');
                submitData.parents_signature = signatureUrl;
            } else if (hasExistingSignature) {
                submitData.parents_signature = existingWaiver.parents_signature;
            }

            if (isEditMode) {
                submitData.id = id;
            }

            submitData.student_id = getEffectiveStudentId(profileData);

            // Show loader during submission
            Swal.fire({
                title: 'Processing...',
                html: 'Saving lunch plan waiver...',
                allowOutsideClick: false,
                allowEscapeKey: false,
                didOpen: () => {
                    Swal.showLoading();
                }
            });

            await dispatch(submitLunchPlanWaiver(submitData)).unwrap();

            Swal.close();

            await Swal.fire({
                icon: 'success',
                title: 'Success',
                text: `Lunch Plan Waiver ${isEditMode ? 'updated' : 'created'} successfully!`
            });

            navigate('/dashboard/lunch-plan-waiver/list');
        } catch (error) {
            Swal.close();
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: error || 'Failed to submit waiver'
            });
        }
    };

    return (
        <Container>
            <Row>
                <Col sm="12">
                    <Card>
                        {remarks &&
                            <Card.Header className="d-flex justify-content-between align-items-center">
                                <div className="header-title">
                                    <h4 className="card-title">
                                        {isEditMode ? 'Edit' : 'Create'} {remarks}
                                    </h4>
                                </div>
                            </Card.Header>
                        }
                        <Card.Body>
                            <Form onSubmit={handleSubmit}>
                                {/* Reason Section */}
                                <h5 className="mb-3">Waiver Information</h5>
                                <Row className="mb-3">
                                    <Col md="12">
                                        <Form.Group className="mb-3">
                                            <Form.Label>Reason for Lunch Plan Waiver *</Form.Label>
                                            <Form.Control
                                                as="textarea"
                                                rows={4}
                                                placeholder="e.g., Child prefers home lunch due to allergy concerns."
                                                value={formData.reason}
                                                onChange={(e) => handleInputChange('reason', e.target.value)}
                                                required
                                            />
                                            <Form.Text className="text-muted">
                                                Please provide a clear reason why you are opting out of the daycare lunch program.
                                            </Form.Text>
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <hr className="my-4" />

                                {/* Signature Section */}
                                <h5 className="mb-3">Parent Signature</h5>
                                <Row className="mb-3">
                                    <Col md="12">
                                        {isEditMode && existingWaiver?.parents_signature ? (
                                            <Form.Group className="mb-3">
                                                <Form.Label>Existing Parent Signature</Form.Label>
                                                <div className="border rounded p-3 bg-light mb-3">
                                                    <img
                                                        src={existingWaiver.parents_signature}
                                                        alt="Existing Parent Signature"
                                                        style={{ maxWidth: '400px', maxHeight: '150px', display: 'block' }}
                                                    />
                                                    <p className="mt-2 mb-0 text-muted">
                                                        <strong>Date:</strong> {existingWaiver.parent_signature_date ? new Date(existingWaiver.parent_signature_date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : 'N/A'}
                                                    </p>
                                                </div>
                                                <p className="text-info mb-2"><small>You can update the signature by drawing a new one below:</small></p>
                                            </Form.Group>
                                        ) : null}
                                        <Form.Group className="mb-3">
                                            <Form.Label>{isEditMode && existingWaiver?.parents_signature ? 'New Parent Signature (Optional)' : 'Parent Signature *'}</Form.Label>
                                            <div className="border rounded p-2 bg-white" style={{ width: '100%', height: '200px' }}>
                                                <SignatureCanvas
                                                    ref={signaturePadRef}
                                                    canvasProps={{
                                                        className: 'signature-canvas',
                                                        style: { width: '100%', height: '100%' }
                                                    }}
                                                />
                                            </div>
                                            <Button
                                                variant="outline-secondary"
                                                size="sm"
                                                className="mt-2"
                                                onClick={handleClearSignature}
                                                type="button"
                                            >
                                                Clear Signature
                                            </Button>
                                        </Form.Group>
                                    </Col>
                                    <Col md="6">
                                        <Form.Group className="mb-3">
                                            <Form.Label>Signature Date *</Form.Label>
                                            <Form.Control
                                                type="date"
                                                value={formData.parent_signature_date}
                                                onChange={(e) => handleInputChange('parent_signature_date', e.target.value)}
                                                max={new Date().toISOString().split('T')[0]}
                                                required
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>

                                {/* Submit Buttons */}
                                <div className="d-flex justify-content-end gap-2 mt-4">
                                    <Button
                                        variant="secondary"
                                        onClick={() => navigate('/dashboard/lunch-plan-waiver/list')}
                                        disabled={submitLoading}
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        variant="primary"
                                        type="submit"
                                        disabled={submitLoading}
                                    >
                                        {submitLoading ? (
                                            <>
                                                <Spinner animation="border" size="sm" className="me-2" />
                                                Submitting...
                                            </>
                                        ) : (
                                            <>{isEditMode ? 'Update' : 'Submit'} Waiver</>
                                        )}
                                    </Button>
                                </div>
                            </Form>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </Container>
    );
};

export default LunchPlanWaiverFormComp;
